import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Users } from 'lucide-react';
import { Button } from '../ui/button';
import { diaEnFrase } from '../../lib/citasApi';
import {
  DIA_SEMANA_CORTO, INICIAL_DIA, MODULOS, motivoVisible, nombresCortos,
  semanasDelMes, textoCelda, textoPacientes, tieneAlgo,
} from '../../lib/agendaMes';
import AvisoHorarioSinRevisar from './AvisoHorarioSinRevisar';

/**
 * La vista «Mes» de la Agenda (dirección, 14/10/2026): «Donde pone Día y
 * Semana en agendas, añade también Mes y una visión del mes»; y antes, «ver un
 * calendario del mes de ese cirujano en el que puedas ver los días que tiene
 * consulta y los que no, tanto si es módulo de mañana o de tarde».
 *
 * Una casilla por día, de lunes a domingo; los días del mes anterior y del
 * siguiente que completan la primera y la última semana, atenuados y sin
 * nada. En cada casilla, por agenda, la MAÑANA («M») y la TARDE («T»):
 *  - verde, con el número de pacientes citados (también el 0), si está
 *    abierta;
 *  - gris y tachada si está cerrada: sin horario ese día, festivo,
 *    vacaciones o un bloqueo (el motivo del bloqueo va debajo);
 *  - a rayas, con borde discontinuo, si está abierta en parte (un bloqueo de
 *    unas horas). Ni amarillo (en la agenda es «online» y «reserva») ni rojo
 *    (cirugía previa).
 *
 * «Abierta» quiere decir que ese módulo tiene horario ese día, no que queden
 * huecos libres (lo dice la leyenda). Mañana y tarde, con el corte de toda la
 * aplicación (`corte_tarde` de la respuesta, CUTOFF_TARDE_MIN del servidor).
 *
 * Con UNA agenda la casilla es grande: «Mañana 6» y «Tarde 0» (o «M» y «T»
 * si no cabe). Con varias, una línea por agenda con lo que tenga ese día:
 * su nombre corto (lo que la distingue, el apellido del profesional, con
 * un distintivo «Qx» si es de quirófano) y «M» y «T». Si la casilla es estrecha, «M»
 * y «T» bajan solos a la línea de debajo del nombre (revisión del
 * 14/10/2026: de 800 a 1366 px el nombre se quedaba en nada). El total de
 * pacientes del día va arriba (cada persona una vez). Si son más de
 * MAX_LINEAS, las primeras y «+N agendas», con el detalle en el título. Un
 * día sin horario y sin nadie no pinta línea.
 *
 * Pinchar un día abre la vista «Día» de esa fecha (`onAbrirDia`). En el
 * MÓVIL (`movil`) las casillas son pequeñas, con dos rayitas (mañana y tarde)
 * o un punto por agenda y el número de pacientes, y tocar un día abre debajo
 * la lista de ese día, con «Abrir el día».
 *
 * Qué está abierto y cuántos pacientes hay lo dice el servidor
 * (GET /api/citas/mes, backend/services/agenda_mes.py); aquí solo se pinta.
 *
 * Arriba, si alguna de las agendas abre de lunes a viernes sin que nadie
 * haya revisado su horario (`horario_por_revisar`, 15/10/2026), el aviso de
 * que el Mes la da como abierta todos los días laborables
 * (AvisoHorarioSinRevisar), con los enlaces para ajustarlo a quien puede
 * configurar agendas.
 *
 * Props:
 *  - mes: «AAAA-MM-01»;
 *  - agendas: las que se enseñan, en su orden;
 *  - datos: la respuesta del servidor (null mientras carga o si falló);
 *  - error: no se pudo cargar (no está «cargando»: el aviso con «Reintentar»
 *    lo pone la página);
 *  - hoy: «AAAA-MM-DD»;
 *  - movil: pantalla estrecha;
 *  - puedeConfigurar: ve los enlaces de configuración del aviso del horario;
 *  - onAbrirDia(fecha).
 */

/** Líneas de agenda por casilla; con más, MAX_LINEAS - 1 y «+N agendas». */
export const MAX_LINEAS = 4;
/** Puntos de agenda por casilla en el móvil. */
const MAX_PUNTOS = 3;

const ESTADOS = new Set(['abierta', 'cerrada', 'parcial']);
const claseEstado = (estado) => `agmes-${ESTADOS.has(estado) ? estado : 'cerrada'}`;
// Las rayas y los puntos del móvil, más pequeños: colores más llenos.
const clasePunto = (estado) => `agmes-punto-${ESTADOS.has(estado) ? estado : 'cerrada'}`;

/** Lo mejor que tiene una agenda ese día, para su punto en el móvil. */
function estadoGeneral(celda) {
  const estados = MODULOS.map(({ clave }) => celda?.[clave]);
  if (estados.includes('abierta')) return 'abierta';
  if (estados.includes('parcial')) return 'parcial';
  return 'cerrada';
}

/**
 * Un módulo (mañana o tarde) de una agenda un día. `etiqueta`: 'corta'
 * («M»), 'adaptable' («Mañana» en pantalla ancha, «M» si no) o 'larga'.
 */
function Modulo({ celda, modulo, etiqueta = 'corta', testId }) {
  const estado = celda[modulo.clave];
  const n = celda[`pacientes_${modulo.clave}`] || 0;
  const cerrada = estado === 'cerrada';
  let texto = modulo.corto;
  if (etiqueta === 'larga') texto = modulo.nombre;
  if (etiqueta === 'adaptable') {
    texto = (
      <>
        <span className="hidden xl:inline">{modulo.nombre}</span>
        <span className="xl:hidden">{modulo.corto}</span>
      </>
    );
  }
  return (
    <span
      className={`${claseEstado(estado)} inline-flex items-center justify-between gap-1 min-w-0 rounded border px-1 text-[11px] leading-4 tabular-nums`}
      data-testid={testId}
      data-estado={estado}
      data-pacientes={n}
    >
      <span className={cerrada ? 'line-through' : undefined}>{texto}</span>
      {/* Abierta (o en parte): siempre el número, también el 0. Cerrada,
          solo si se quedó alguien citado. */}
      {(!cerrada || n > 0) && <span className="font-semibold">{n}</span>}
    </span>
  );
}

function Leyenda({ corte }) {
  const muestra = 'inline-flex items-center gap-1 rounded border px-1 text-[11px] leading-4 tabular-nums';
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#13231E] mb-2"
      data-testid="agenda-mes-leyenda">
      <span className="inline-flex items-center gap-1.5">
        <span className={`${muestra} agmes-abierta`} aria-hidden="true">M <b>6</b></span>
        Abierta, con sus pacientes citados
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className={`${muestra} agmes-parcial`} aria-hidden="true">M <b>2</b></span>
        Abierta en parte (bloqueo de unas horas)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className={`${muestra} agmes-cerrada`} aria-hidden="true"><span className="line-through">M</span></span>
        Cerrada: sin horario, festivo, vacaciones o bloqueo
      </span>
      {/* El corte lo dice el servidor (es el de toda la aplicación); mientras
          no ha llegado, sin hora. */}
      <span data-testid="agenda-mes-corte">
        {corte ? `M = Mañana: antes de las ${corte} · T = Tarde: desde las ${corte}` : 'M = Mañana · T = Tarde'}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-4 h-4 rounded ring-2 ring-[#13231E] dark:ring-white" aria-hidden="true" />
        Hoy
      </span>
      <span className="w-full text-[11px] text-muted-foreground" data-testid="agenda-mes-leyenda-abierta">
        «Abierta» quiere decir que hay horario de consulta, no que queden huecos libres.
      </span>
    </div>
  );
}

export default function AgendaMes({
  mes, agendas = [], datos = null, error = false, hoy = '', movil = false, puedeConfigurar = false,
  onAbrirDia,
}) {
  // En el móvil, el día tocado: su lista sale debajo de la rejilla.
  const [elegido, setElegido] = useState(null);
  const detalleRef = useRef(null);
  const semanas = useMemo(() => semanasDelMes(mes), [mes]);
  const una = agendas.length === 1;
  const cortos = useMemo(() => nombresCortos(agendas), [agendas]);
  const elegidoVigente = movil && elegido && elegido.slice(0, 7) === mes.slice(0, 7) ? elegido : null;

  useEffect(() => {
    if (elegidoVigente) detalleRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [elegidoVigente]);

  // Lo de cada agenda ese día (solo las que han llegado) y las que tienen algo.
  const filasDe = (fecha) => agendas
    .map((agenda) => ({ agenda, celda: datos?.dias?.[fecha]?.[agenda.id] || null }))
    .filter((f) => f.celda);
  const totalDe = (fecha, filas) => (una
    ? (filas[0]?.celda?.pacientes || 0)
    : (Number(datos?.pacientes_por_dia?.[fecha]) || 0));

  const pinchar = (fecha) => {
    if (movil) setElegido((prev) => (prev === fecha ? null : fecha));
    else onAbrirDia?.(fecha);
  };

  const casilla = ({ fecha, delMes }) => {
    const numero = Number(fecha.slice(8, 10));
    if (!delMes) {
      return (
        <div
          key={fecha}
          aria-hidden="true"
          className={`rounded-lg border border-dashed border-[#D9E3DD] p-1 text-xs tabular-nums text-[#13231E]/30 dark:text-white/30 ${movil ? 'h-14' : 'min-h-[5.5rem]'}`}
          data-testid={`agenda-mes-otro-${fecha}`}
        >
          {numero}
        </div>
      );
    }
    const esHoy = fecha === hoy;
    const filas = filasDe(fecha);
    const conAlgo = filas.filter((f) => tieneAlgo(f.celda));
    const total = totalDe(fecha, filas);
    const cabeza = `${diaEnFrase(fecha)}${esHoy ? ', hoy' : ''}`;
    let etiqueta = cabeza;
    let titulo;
    if (datos && una && filas[0]) {
      etiqueta = `${cabeza}: ${textoCelda(filas[0].celda)}`;
      titulo = etiqueta;
    } else if (datos && !una) {
      const lineas = conAlgo.map((f) => `${f.agenda.name}: ${textoCelda(f.celda)}`);
      const resumen = conAlgo.length ? `${textoPacientes(total)} en total` : 'sin consulta en ninguna agenda';
      etiqueta = `${cabeza}: ${[resumen, ...lineas].join('. ')}`;
      titulo = [`${cabeza}: ${resumen}`, ...lineas].join('\n');
    }
    const elegida = elegidoVigente === fecha;
    const marco = `${esHoy ? 'ring-2 ring-[#13231E] dark:ring-white' : ''} ${elegida ? 'border-[#2E6E5E] bg-[rgba(46,110,94,0.08)]' : 'border-[#D9E3DD] bg-white'}`;

    if (movil) {
      return (
        <button
          key={fecha}
          type="button"
          onClick={() => pinchar(fecha)}
          aria-label={etiqueta}
          aria-pressed={elegida}
          className={`relative flex flex-col items-center justify-between h-14 min-w-0 rounded-lg border p-1 ${marco}`}
          data-testid={`agenda-mes-dia-${fecha}`}
          data-hoy={esHoy ? '1' : undefined}
        >
          <span className="text-xs font-semibold tabular-nums text-[#13231E]">{numero}</span>
          {una && filas[0] && tieneAlgo(filas[0].celda) && (
            <span className="flex gap-0.5" aria-hidden="true">
              {MODULOS.map((m) => (
                <span key={m.clave} className={`${clasePunto(filas[0].celda[m.clave])} h-1.5 w-2.5 rounded-full border`}
                  data-testid={`agenda-mes-raya-${fecha}-${m.clave}`} data-estado={filas[0].celda[m.clave]} />
              ))}
            </span>
          )}
          {!una && conAlgo.length > 0 && (
            <span className="flex items-center gap-0.5" aria-hidden="true">
              {conAlgo.slice(0, MAX_PUNTOS).map((f) => (
                <span key={f.agenda.id} className={`${clasePunto(estadoGeneral(f.celda))} h-1.5 w-1.5 rounded-full border`} />
              ))}
              {conAlgo.length > MAX_PUNTOS && <span className="text-[9px] leading-none text-[#13231E]/70">+</span>}
            </span>
          )}
          <span className={`text-[10px] leading-none font-semibold tabular-nums text-[#13231E] ${total > 0 ? '' : 'invisible'}`}
            aria-hidden="true" data-testid={`agenda-mes-total-${fecha}`}>
            {total}
          </span>
        </button>
      );
    }

    const sobran = conAlgo.length > MAX_LINEAS ? conAlgo.length - (MAX_LINEAS - 1) : 0;
    const visibles = sobran ? conAlgo.slice(0, MAX_LINEAS - 1) : conAlgo;
    const motivo = una && filas[0] ? motivoVisible(filas[0].celda) : null;
    return (
      <button
        key={fecha}
        type="button"
        onClick={() => pinchar(fecha)}
        aria-label={etiqueta}
        title={titulo}
        className={`relative flex flex-col items-stretch gap-1 min-w-0 rounded-lg border p-1.5 text-left transition-colors hover:border-[#2E6E5E] hover:bg-[rgba(46,110,94,0.04)] ${una ? 'min-h-[5.5rem]' : 'min-h-[6.5rem]'} ${marco}`}
        data-testid={`agenda-mes-dia-${fecha}`}
        data-hoy={esHoy ? '1' : undefined}
      >
        <span className="flex items-center justify-between gap-1">
          <span className="text-xs font-semibold tabular-nums text-[#13231E]">{numero}</span>
          {total > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums text-[#13231E]/80"
              aria-hidden="true" data-testid={`agenda-mes-total-${fecha}`}>
              <Users className="w-3 h-3" />
              {total}
            </span>
          )}
        </span>
        {datos && una && filas[0] && (tieneAlgo(filas[0].celda) ? (
          <span className="flex flex-col gap-1 min-w-0">
            {MODULOS.map((m) => (
              <Modulo key={m.clave} celda={filas[0].celda} modulo={m} etiqueta="adaptable"
                testId={`agenda-mes-modulo-${fecha}-${filas[0].agenda.id}-${m.clave}`} />
            ))}
            {motivo && (
              <span className="text-[10px] leading-tight text-muted-foreground truncate" data-testid={`agenda-mes-motivo-${fecha}`}>
                {motivo}
              </span>
            )}
          </span>
        ) : (
          <span className="text-[10px] text-muted-foreground">Sin consulta</span>
        ))}
        {datos && !una && (
          <span className="flex flex-col gap-0.5 min-w-0">
            {visibles.map(({ agenda, celda }) => {
              const corto = cortos[agenda.id] || { principal: agenda.name, tipo: null };
              return (
                // El nombre y, si cabe, «M» y «T» a su lado; si no cabe
                // (casilla estrecha), «M» y «T» bajan a la línea siguiente y
                // el nombre se queda con todo el ancho (flex-wrap).
                <span key={agenda.id} className="flex flex-wrap items-center gap-x-1 min-w-0"
                  title={agenda.name}
                  data-testid={`agenda-mes-linea-${fecha}-${agenda.id}`}>
                  <span className="flex flex-1 basis-[5.5rem] items-center gap-1 min-w-0">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: agenda.color || '#2E6E5E' }} aria-hidden="true" />
                    <span className="min-w-0 truncate text-[11px] leading-4 font-medium text-[#13231E]"
                      data-testid={`agenda-mes-nombre-${fecha}-${agenda.id}`}>
                      {corto.principal}
                    </span>
                    {corto.tipo && (
                      <span className="shrink-0 rounded border border-[#13231E]/25 px-0.5 text-[9px] font-semibold leading-3 text-[#13231E]/80 dark:border-white/30"
                        data-testid={`agenda-mes-tipo-${fecha}-${agenda.id}`}>
                        {corto.tipo}
                      </span>
                    )}
                  </span>
                  <span className="ml-auto flex shrink-0 items-center gap-0.5">
                    {MODULOS.map((m) => (
                      <Modulo key={m.clave} celda={celda} modulo={m}
                        testId={`agenda-mes-modulo-${fecha}-${agenda.id}-${m.clave}`} />
                    ))}
                  </span>
                </span>
              );
            })}
            {sobran > 0 && (
              <span
                className="text-[11px] leading-4 font-medium text-[#2E6E5E] dark:text-primary"
                title={conAlgo.slice(MAX_LINEAS - 1).map((f) => `${f.agenda.name}: ${textoCelda(f.celda)}`).join('\n')}
                data-testid={`agenda-mes-mas-${fecha}`}
              >
                +{sobran} {sobran === 1 ? 'agenda' : 'agendas'}
              </span>
            )}
            {!conAlgo.length && <span className="text-[10px] text-muted-foreground">Sin consulta</span>}
          </span>
        )}
      </button>
    );
  };

  const filasElegido = elegidoVigente ? filasDe(elegidoVigente) : [];
  const listaElegido = una ? filasElegido : filasElegido.filter((f) => tieneAlgo(f.celda));
  const fraseElegido = elegidoVigente ? diaEnFrase(elegidoVigente) : '';

  return (
    <div data-testid="agenda-mes" aria-busy={!datos && !error}>
      <AvisoHorarioSinRevisar agendas={agendas} puedeConfigurar={puedeConfigurar} />
      <Leyenda corte={datos?.corte_tarde || null} />
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-1 text-center text-[11px] font-medium text-muted-foreground" aria-hidden="true">
        {(movil ? INICIAL_DIA : DIA_SEMANA_CORTO).map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5" data-testid="agenda-mes-rejilla">
        {semanas.flat().map(casilla)}
      </div>

      {elegidoVigente && (
        <section
          ref={detalleRef}
          className="mt-3 rounded-xl border border-[#D9E3DD] bg-white p-3"
          aria-live="polite"
          data-testid="agenda-mes-detalle"
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="text-sm font-semibold text-[#13231E]">
              {fraseElegido.charAt(0).toUpperCase()}{fraseElegido.slice(1)}
            </h3>
            <Button size="sm" className="h-8 bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              onClick={() => onAbrirDia?.(elegidoVigente)} data-testid="agenda-mes-abrir-dia">
              Abrir el día ▸
            </Button>
          </div>
          {!datos ? (
            <p className="text-xs text-muted-foreground">Cargando…</p>
          ) : !listaElegido.length || (una && !tieneAlgo(listaElegido[0].celda)) ? (
            <p className="text-sm text-muted-foreground">
              {una ? 'Sin consulta este día.' : 'Ninguna agenda tiene consulta este día.'}
            </p>
          ) : (
            <ul className="space-y-2.5">
              {listaElegido.map(({ agenda, celda }) => {
                const motivo = motivoVisible(celda);
                return (
                  <li key={agenda.id} data-testid={`agenda-mes-detalle-${agenda.id}`}>
                    <div className="flex items-center gap-1.5 text-sm font-medium text-[#13231E] min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: agenda.color || '#2E6E5E' }} aria-hidden="true" />
                      <span className="truncate">{agenda.name}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {MODULOS.map((m) => (
                        <Modulo key={m.clave} celda={celda} modulo={m} etiqueta="larga" />
                      ))}
                    </div>
                    {motivo && <div className="mt-0.5 text-xs text-muted-foreground">{motivo}</div>}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
